import { ActionContext, ActionTree } from 'vuex';
import { RootState } from '@/store';
import { CustomerState } from '@/store/modules/backoffice/customer/index';
import CustomerService from '@/services/backoffice.customer.service';
import {
  CREATE_MANAGER,
  CREATE_REMINDERS, CUSTOMER_DETAILS, CUSTOMER_ROOT,
  DELETE_MANAGER,
  DELETE_REMINDERS,
  EXPORT_FORM_101,
  GET_ALL_CUSTOMERS,
  GET_ALL_OFFICES_BY_CUSTOMER,
  GET_ALL_SECTIONS_BY_OFFICE,
  GET_ALL_REMINDERS_BY_CUSTOMER,
  GET_CUSTOMER_DETAILS,
  GET_MANAGER_PRIVILEGES,
  GET_MANAGERS_BY_CUSTOMER,
  GET_OFFICE_DETAILS,
  LIST_ORG_UNITS,
  SEARCH_MANAGER,
  SET_ALL_CUSTOMERS,
  SET_ALL_CUSTOMERS_ERRORS,
  SET_ALL_OFFICES_BY_CUSTOMER,
  SET_ALL_OFFICES_ERRORS,
  SET_ALL_REMINDERS_BY_CUSTOMER,
  SET_CUSTOMER_DETAILS,
  SET_EXPORT_FORM_101_REQUEST_ERRORS,
  SET_MANAGER_PRIVILEGES,
  SET_MANAGER_TO_ADD,
  SET_MANAGERS_BY_CUSTOMER,
  SET_OFFICE_DETAILS,
  SET_OPEN_ADD_MANAGER_MODAL,
  SET_OPEN_ADD_MANAGER_MODAL_ERRORS,
  SET_OPEN_CREATE_REMINDERS_MODAL,
  SET_OPEN_CREATE_REMINDERS_MODAL_ERRORS,
  SET_OPEN_DELETE_MANAGER_MODAL,
  SET_OPEN_DELETE_MANAGER_MODAL_ERRORS,
  SET_OPEN_UPDATE_MANAGER_MODAL,
  SET_OPEN_UPDATE_MANAGER_MODAL_ERRORS,
  SET_OPEN_UPLOAD_MANAGER_PRIVILEGES_FILE_MODAL,
  SET_ORG_UNITS,
  SET_UPDATE_CUSTOMER_SETTINGS_MODAL,
  SET_UPDATE_CUSTOMER_SETTINGS_MODAL_ERRORS,
  SET_UPDATE_OFFICE_SETTINGS_MODAL,
  SET_UPDATE_OFFICE_SETTINGS_MODAL_ERRORS,
  SET_UPLOAD_MANAGER_PRIVILEGES_FILE_MODAL_ERRORS,
  UPDATE_CUSTOMER,
  UPDATE_MANAGER,
  UPDATE_OFFICE,
  UPLOAD_MANAGER_PRIVILEGES_FILE,
  SET_ALL_SECTIONS_BY_OFFICE,
  SET_ALL_SECTIONS_ERRORS,
} from '@/types/customer.types';
import { Grid } from '@/models/Grid.model';
import { CustomerDetailsModel } from '@/models/backoffice/CustomerDetails';
import { eventsHub } from '@/main';
import { RequestPayload } from '@/models/base/RequestPayload';
import { DeleteRemindersRequest } from '@/models/backoffice/DeleteRemindersRequest';
import { CreateRemindersRequest } from '@/models/backoffice/CreateRemindersRequest';
import { ManagerPrivilegesRequest } from '@/models/backoffice/ManagerPrivilegesRequest';
import { SearchManagerRequest } from '@/models/backoffice/SearchManagerRequest';
import { CreateManagerPrivilegesModel } from '@/models/backoffice/CreateManagerPrivilegesModel';
import { OrganisationUnitsRequestModel } from '@/models/backoffice/OrganisationUnitsRequestModel';
import { finalize, pluck } from 'rxjs/operators';
import Mock from '@/globals/backoffice.mock';
import { ExportForm101RequestModel } from '@/models/backoffice/ExportForm101RequestModel';
import { SET_MANAGER_LOADING } from '@/types/managerForm.types';
import { AUTHENTICATION_ROOT, USER_DETAILS } from '@/types/authentication.types';

export const customerService = new CustomerService();

export const actions: ActionTree<CustomerState, RootState> = {

  [GET_ALL_CUSTOMERS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
  ) => {
    customerService.getAllCustomers()
      .subscribe(
        data => {
          const customersGrid = {
            results: data.customers,
            resultCount: data.customers!.length || 0,
          } as Grid;
          commit(SET_ALL_CUSTOMERS, customersGrid);
        },
        error => commit(SET_ALL_CUSTOMERS_ERRORS, error.messages),
      );
  },

  [GET_CUSTOMER_DETAILS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    indexNum: number,
  ) => {
    customerService.getCustomerDetails(indexNum)
      .subscribe(
        data => {
          commit(SET_CUSTOMER_DETAILS, data);
          eventsHub.$router.push({ name: 'customer-details' });
        },
        error => commit(SET_ALL_CUSTOMERS_ERRORS, error.messages),
      );
  },

  [UPDATE_CUSTOMER]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    customerToUpdate: CustomerDetailsModel,
  ) => {
    commit(SET_UPDATE_CUSTOMER_SETTINGS_MODAL_ERRORS, []);
    customerService.updateCustomer(customerToUpdate)
      .subscribe(
        () => {
          commit(SET_UPDATE_CUSTOMER_SETTINGS_MODAL, false);
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('back_office.settings.customer.customerUpdatedSuccessfully'),
          });
          dispatch(GET_CUSTOMER_DETAILS, customerToUpdate.indexNum);
        },
        error => commit(SET_UPDATE_CUSTOMER_SETTINGS_MODAL_ERRORS, error.messages),
      );
  },

  [GET_ALL_OFFICES_BY_CUSTOMER]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    indexNum: number,
  ) => {
    customerService.getAllOfficesByCustomer(indexNum)
      .subscribe(
        data => {
          const officesGrid = {
            results: data.offices,
            resultCount: data.offices!.length || 0,
          } as Grid;
          commit(SET_ALL_OFFICES_BY_CUSTOMER, officesGrid);
        },
        error => commit(SET_ALL_OFFICES_ERRORS, error.messages));
  },

  [GET_ALL_SECTIONS_BY_OFFICE]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    officeNum: number,
  ) => {
    customerService.getAllSectionsByOffice(officeNum)
      .subscribe(
        data => {
          const sectionsGrid = {
            results: data.sections,
            resultCount: data.sections!.length || 0,
          } as Grid;
          commit(SET_ALL_SECTIONS_BY_OFFICE, sectionsGrid);
        },
        error => commit(SET_ALL_SECTIONS_ERRORS, error.messages));
  },

  [GET_OFFICE_DETAILS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    { requestBody }: RequestPayload,
  ) => {
    customerService.getOfficeDetails(requestBody.indexNum, requestBody.officeNum)
      .subscribe(
        data => {
          commit(SET_OFFICE_DETAILS, data);
          commit(SET_UPDATE_OFFICE_SETTINGS_MODAL, true);
          commit(SET_UPDATE_OFFICE_SETTINGS_MODAL_ERRORS, []);
        },
        error => commit(SET_ALL_CUSTOMERS_ERRORS, error.messages),
      );
  },

  [UPDATE_OFFICE]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    officeToUpdate: CustomerDetailsModel,
  ) => {
    customerService.updateOffice(officeToUpdate)
      .subscribe(
        () => {
          commit(SET_UPDATE_OFFICE_SETTINGS_MODAL, false);
          commit(SET_OFFICE_DETAILS, {} as CustomerDetailsModel);
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('back_office.settings.offices.officeUpdatedSuccessfully'),
          });
          dispatch(GET_ALL_OFFICES_BY_CUSTOMER, officeToUpdate.indexNum);
        },
        error => commit(SET_UPDATE_OFFICE_SETTINGS_MODAL_ERRORS, error.messages),
      );
  },

  [GET_ALL_REMINDERS_BY_CUSTOMER]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    indexNum: number,
  ) => {
    const remindersMock = Mock.reminders.reminderTypes;
    customerService.getAllRemindersByCustomer(indexNum)
      .subscribe(
        (data) => {
          data.reminders!.forEach((reminder: any) => {
            remindersMock.forEach((reminderType) => {
              if (reminderType.value === reminder.reminderType) {
                reminder.reminderTypeDesc = reminderType.description;
              }
            });
          });
          const remindersGrid = {
            results: data.reminders,
            resultCount: data.reminders!.length || 0,
          } as Grid;
          commit(SET_ALL_REMINDERS_BY_CUSTOMER, remindersGrid);
        },
        error => commit(SET_UPDATE_OFFICE_SETTINGS_MODAL_ERRORS, error.messages),
      );
  },

  [CREATE_REMINDERS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    createRemindersRequest: CreateRemindersRequest,
  ) => {
    customerService.createReminders(createRemindersRequest)
      .subscribe(
        (data) => {
          commit(SET_OPEN_CREATE_REMINDERS_MODAL, false);
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('back_office.settings.reminders.remindersAddedSuccessfully'),
          });
          dispatch(GET_ALL_REMINDERS_BY_CUSTOMER, state.customerDetails.indexNum);
        },
        error => commit(SET_OPEN_CREATE_REMINDERS_MODAL_ERRORS, error.messages),
      );
  },

  [DELETE_REMINDERS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    deleteRemindersRequest: DeleteRemindersRequest,
  ) => {
    customerService.deleteReminders(deleteRemindersRequest)
      .subscribe(
        data => {
          commit(SET_OPEN_CREATE_REMINDERS_MODAL, false);
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('back_office.settings.reminders.remindersRemovedSuccessfully'),
          });
          dispatch(GET_ALL_REMINDERS_BY_CUSTOMER, state.customerDetails.indexNum);
        },
        error => commit(SET_UPDATE_OFFICE_SETTINGS_MODAL_ERRORS, error.messages),
      );
  },

  [GET_MANAGERS_BY_CUSTOMER]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    indexNum: number,
  ) => {
    commit(SET_MANAGERS_BY_CUSTOMER, {});
    customerService.getManagersByCustomer(indexNum)
      .pipe(
        pluck('managers'),
      )
      .subscribe(
        (data: any) => {
          const managers = data.map((manager) => {
            if (manager.empFormsPriv === 1) {
              manager.empFormsPrivDesc = eventsHub.$t('back_office.settings.managers.MANAGER_VIEW_ONLY');
            } else if (manager.empFormsPriv === 2) {
              manager.empFormsPrivDesc = eventsHub.$t('back_office.settings.managers.MANAGER_APPROVE');
            }
            return manager;
          });
          const managersGrid = {
            results: managers,
            resultCount: managers!.length || 0,
          } as Grid;
          commit(SET_MANAGERS_BY_CUSTOMER, managersGrid);
        },
        error => commit(SET_UPDATE_OFFICE_SETTINGS_MODAL_ERRORS, error.messages),
      );
  },

  [DELETE_MANAGER]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    managerToDelete: ManagerPrivilegesRequest,
  ) => {
    customerService.deleteManager(managerToDelete)
      .subscribe(
        data => {
          commit(SET_OPEN_DELETE_MANAGER_MODAL, false);
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('back_office.settings.managers.managerRemovedSuccessfully'),
          });
          dispatch(GET_MANAGERS_BY_CUSTOMER, state.customerDetails.indexNum);
        },
        error => commit(SET_OPEN_DELETE_MANAGER_MODAL_ERRORS, error.messages),
      );
  },

  [UPDATE_MANAGER]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
  ) => {
    customerService.updateManagerPrivileges(state.managerPrivileges)
      .subscribe(
        data => {
          commit(SET_OPEN_UPDATE_MANAGER_MODAL, false);
          commit(SET_ORG_UNITS, undefined);
          dispatch(GET_MANAGERS_BY_CUSTOMER, state.customerDetails.indexNum);
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('back_office.settings.managers.managerUpdatedSuccessfully'),
          });
        },
        error => commit(SET_OPEN_UPDATE_MANAGER_MODAL_ERRORS, error.messages),
      );
  },

  [GET_MANAGER_PRIVILEGES]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    managerToUpdate: ManagerPrivilegesRequest,
  ) => {
    customerService.getManagerPrivileges(managerToUpdate)
      .subscribe(
        data => {
          commit(SET_MANAGER_PRIVILEGES, data);
          commit(SET_OPEN_UPDATE_MANAGER_MODAL, true);

          // eventsHub.$notify({
          //   group: 'frd',
          //   type: 'success',
          //   text: '' + eventsHub.$t('back_office.settings.managers.managerRemovedSuccessfully'),
          // });
          // dispatch(GET_MANAGERS_BY_CUSTOMER, state.customerDetails.indexNum);
        },
        error => commit(SET_OPEN_UPDATE_MANAGER_MODAL_ERRORS, error.messages),
      );
  },

  [SEARCH_MANAGER]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    searchManagerRequest: SearchManagerRequest,
  ) => {
    customerService.searchManager(searchManagerRequest)
      .subscribe(
        (data) => {
          const grid: any = {
            results: [
              {
                indexNum: data.indexNum,
                officeNum: data.officeNum,
                officeName: data.officeName,
                userId: data.userId,
                idNumber: data.idNumber,
                firstName: data.firstName,
                lastName: data.lastName,
                fullName: data.fullName,
                numAndName: data.numAndName,
              },
            ],
            resultCount: 1,
          };
          commit(SET_MANAGER_TO_ADD, grid);
        },
        error => commit(SET_OPEN_ADD_MANAGER_MODAL_ERRORS, error.messages),
      );
  },

  [CREATE_MANAGER]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    createManagerPrivilegesModel: CreateManagerPrivilegesModel,
  ) => {
    customerService.createManager(createManagerPrivilegesModel)
      .subscribe(
        data => {
          commit(SET_OPEN_ADD_MANAGER_MODAL, false);
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('back_office.settings.managers.managerAddedSuccessfully'),
          });
          dispatch(GET_MANAGERS_BY_CUSTOMER, state.customerDetails.indexNum);
        },
        error => commit(SET_OPEN_ADD_MANAGER_MODAL_ERRORS, error.messages),
      );
  },

  [LIST_ORG_UNITS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    organisationUnitsRequest: OrganisationUnitsRequestModel,
  ) => {
    customerService.getOrganisationUnitsList(organisationUnitsRequest)
      .subscribe(
        (data: any) => {
          commit(SET_ORG_UNITS, {
            index: organisationUnitsRequest.index,
            officeNum: organisationUnitsRequest.officeNum,
            orgUnitType: organisationUnitsRequest.orgUnitType,
            units: data.bodyList,
          });
        },
        error => {
          commit(SET_ORG_UNITS, undefined);
          commit(SET_OPEN_UPDATE_MANAGER_MODAL_ERRORS, error.messages);
        },
      );
  },

  [EXPORT_FORM_101]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    exportForm101Request: ExportForm101RequestModel,
  ) => {
    customerService.createExportForm101Request(exportForm101Request)
      .subscribe(
        (data) => {
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('back_office.export_form_101.exportForm101RequestCreatedSuccessfully'),
          });
        },
        error => commit(SET_EXPORT_FORM_101_REQUEST_ERRORS, error.messages),
      );
  },

  [UPLOAD_MANAGER_PRIVILEGES_FILE]: (
    { commit, dispatch, state, rootGetters }: ActionContext<CustomerState, RootState>,
    { requestBody }: RequestPayload,
  ) => {
    commit(`manager/${SET_MANAGER_LOADING}`, true, { root: true });
    customerService.uploadManagerPrivilegesFile(
      requestBody.file,
      requestBody.indexNum,
      requestBody.replace,
      requestBody.type
    )
      .pipe(
      finalize(() => commit(`manager/${SET_MANAGER_LOADING}`, false, { root: true })),
    )
      .subscribe(
        (data) => {
          commit(SET_OPEN_UPLOAD_MANAGER_PRIVILEGES_FILE_MODAL, false)
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('back_office.settings.managers.managerPrivilegesUpdatedSuccessfully'),
          });
          const indexNum = rootGetters[CUSTOMER_ROOT + CUSTOMER_DETAILS].indexNum;
          dispatch(GET_MANAGERS_BY_CUSTOMER, indexNum);
        },
    error => commit(SET_UPLOAD_MANAGER_PRIVILEGES_FILE_MODAL_ERRORS, error.messages),
      );
  },
};
